/**
 * PWS × AWARE Conference Interest — Thank-you confirmation page
 *
 * Public route: /pws-partner/thank-you (reached after submitting the
 * expression-of-interest form on /pws-partner).
 *
 * Deliberately kept lightweight — the important thing is that the
 * franchisee sees a clear confirmation with two calls to action:
 * open the digital Partner Guide now, or return to the site.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, BookOpen, Home, ArrowRight, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { BrandLogo } from "@/components/BrandLogo";

export default function PwsPartnerConferenceThankYou() {
  useSEO({
    title: "Thank you — PWS × AWARE Partner Network",
    noindex: true,
    includeBaseline: false,
  });
  const location = useLocation();
  // The landing page hands over the actual email-send status via
  // location.state — falls back to "email sent" for direct-hits that
  // never went through the form (rare, but avoids showing a scary
  // fallback message to someone who deep-linked here).
  const emailSent = location.state?.emailSent !== false;
  const serverMessage = location.state?.serverMessage;
  const [guideUrl, setGuideUrl] = useState("/pws-partner/guide/index.html");
  const [pdfUrl, setPdfUrl] = useState("/pws-partner/PWS-AWARE-Partner-Guide.pdf");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/pws-partner/config");
        if (cancelled) return;
        // Feb 2026 — link straight to the static flipbook + PDF assets
        // so the browser never touches the React SPA (which was
        // surfacing "You need to enable JavaScript" in production) or
        // the backend flag-gated PDF endpoint (which was returning
        // 404 → "Blocked" for anon).
        setGuideUrl(data?.flipbook_url || "/pws-partner/guide/index.html");
        setPdfUrl(data?.pdf_url || "/pws-partner/PWS-AWARE-Partner-Guide.pdf");
      } catch {
        /* keep defaults */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#031533] via-[#062754] to-[#0a3878] text-white flex flex-col">
      <div className="mx-auto max-w-2xl px-6 pt-14 pb-24 flex-1 w-full">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-9 w-auto" />
          <span className="text-sm text-cyan-200/80 hidden md:inline">
            awarewatertesting.com.au
          </span>
        </div>

        <div
          data-testid="pws-conference-thankyou"
          className="mt-14 rounded-2xl border border-cyan-200/15 bg-white/5 backdrop-blur-sm p-8 md:p-12 shadow-2xl shadow-cyan-950/40"
        >
          <div className={`inline-flex h-12 w-12 items-center justify-center rounded-full ${emailSent ? "bg-cyan-400 text-slate-900" : "bg-amber-400 text-slate-900"}`}>
            {emailSent ? <CheckCircle2 className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
          </div>
          <h1 className="mt-5 text-3xl md:text-4xl font-semibold tracking-tight">
            {emailSent
              ? "Thank you — your PWS × AWARE Partner Guide is on its way."
              : "Thank you — we've received your request."}
          </h1>
          <p
            className="mt-5 text-cyan-50/85 leading-relaxed"
            data-testid="pws-conference-thankyou-message"
          >
            {emailSent ? (
              serverMessage ||
              "Please check your email for the digital guide. Stephen or the AWARE team will contact you if you requested information about activating a Partner Portal account."
            ) : (
              "Our system saved your details but the Partner Guide email couldn't be dispatched right now. The AWARE team has been alerted and will follow up with your guide as soon as possible — you don't need to submit again."
            )}
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link
              to={guideUrl}
              data-testid="pws-conference-thankyou-open-guide"
              className="inline-flex items-center justify-center gap-2 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold px-6 py-3 rounded-full transition-colors"
            >
              <BookOpen className="h-4 w-4" /> Open the Digital Partner Guide
            </Link>
            <Link
              to="/"
              data-testid="pws-conference-thankyou-home"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/25 text-white/90 hover:bg-white/10 transition-colors text-sm"
            >
              <Home className="h-4 w-4" /> Return to AWARE Water Testing
            </Link>
          </div>

          {pdfUrl && (
            <p className="mt-8 text-sm text-cyan-100/70">
              Prefer a downloadable copy? <a
                href={pdfUrl}
                className="inline-flex items-center gap-1 underline hover:text-cyan-200"
                data-testid="pws-conference-thankyou-pdf"
              >
                Download the PDF <ArrowRight className="h-3 w-3" />
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
